import { supabase } from '@/integrations/supabase/client';
import { UserSupplement } from '@/types/user';

export interface DatabaseSupplement {
  id: string;
  name: string;
  brand_name?: string;
  dosage?: string;
  timing?: 'morning' | 'afternoon' | 'evening' | 'anytime';
  suggested_time?: string;
  icon?: string;
  is_active: boolean;
  user_id: string;
  brand_id: string;
  created_at: string;
  updated_at: string;
}

export interface SupplementBrand {
  id: string;
  name: string;
}

// Predefined supplement brands (alphabetically ordered)
export const SUPPLEMENT_BRANDS = [
  '1st Phorm',
  '1UP Nutrition',
  '21st Century',
  '4Life',
  'Alani Nu',
  'Align',
  'Amazing Grass',
  'Ancient Nutrition',
  'Arbonne',
  'Athletic Greens (AG1)',
  'Balance of Nature',
  'Bonafide',
  'Bronson Vitamins',
  'BSN',
  'Bulletproof',
  'Carlson',
  'Cellucor',
  'Codeage',
  'Country Life',
  'Culturelle',
  'Designs for Health',
  'Doctor\'s Best',
  'Douglas Laboratories',
  'doTERRA',
  'Dymatize',
  'Enzymedica',
  'Gaia Herbs',
  'Garden of Life',
  'Ghost Lifestyle',
  'GNC',
  'Goli Nutrition',
  'Herbalife',
  'HUM Nutrition',
  'Jarrow Formulas',
  'Kaged',
  'Kal',
  'Kirkland Signature',
  'Level Life Extension',
  'Liquid I.V.',
  'Mannatech',
  'MaryRuth Organics',
  'MegaFood',
  'Metagenics',
  'Momentous',
  'MuscleTech',
  'Natrol',
  'Nature Made',
  'Nature\'s Bounty',
  'Nature\'s Sunshine',
  'Nature\'s Truth',
  'Nature\'s Way',
  'Natural Factors',
  'Neocell',
  'New Chapter',
  'Nordic Naturals',
  'NutraBio',
  'Nutrafol',
  'Nutrigold',
  'Nutrilite',
  'Nuun',
  'Olly',
  'One A Day',
  'Onnit',
  'Optimum Nutrition',
  'Orgain',
  'Ortho Molecular Products',
  'Osteo Bi-Flex',
  'Persona Nutrition',
  'Physician\'s Choice',
  'Plexus Worldwide',
  'Pure Encapsulations',
  'Puritan\'s Pride',
  'Qunol',
  'Rainbow Light',
  'Renew Life',
  'Ritual',
  'Schiff Vitamins',
  'Shaklee',
  'SmartyPants',
  'Solgar',
  'Source Naturals',
  'Sports Research',
  'Spring Valley',
  'Standard Process',
  'Sunwarrior',
  'Swanson',
  'Thorne',
  'Trace Minerals',
  'Transparent Labs',
  'Tru Niagen',
  'Truvani',
  'Twinlab',
  'Uqora',
  'USANA',
  'Vega',
  'Vitafusion',
  'Vital Nutrients',
  'Vital Proteins',
  'Young Living',
  'Youtheory'
];

// Simple icon identifiers for supplement types
export const SUPPLEMENT_ICONS = [
  'tablet',
  'capsule', 
  'liquid',
  'powder',
  'injection',
  'cream',
  'chewable',
  'gummy',
  'patch',
  'sublingual',
  'suppository',
  'enema',
  'energy',
  'focus',
  'heart',
  'brain'
];

export const supplementService = {
  // Get user's supplements
  async getUserSupplements(userId: string, brandId: string): Promise<UserSupplement[]> {
    const { data, error } = await supabase
      .from('user_supplements')
      .select('*')
      .eq('user_id', userId)
      .eq('brand_id', brandId)
      .eq('active', true);

    if (error) throw error;

    return data.map(supplement => ({
      id: supplement.id,
      name: supplement.name,
      brand: supplement.brand,
      dosage: supplement.dosage || '',
      timing: (supplement.timing as 'morning' | 'afternoon' | 'evening' | 'anytime') || 'anytime',
      suggestedTime: supplement.suggested_time || '08:00',
      icon: supplement.icon || '💊',
      active: supplement.active,
      addedAt: supplement.added_at
    }));
  },

  // Add new supplement
  async addSupplement(
    userId: string, 
    brandId: string, 
    supplement: Omit<UserSupplement, 'id' | 'addedAt' | 'active'>
  ): Promise<UserSupplement> {
    const { data, error } = await supabase
      .from('user_supplements')
      .insert({
        user_id: userId,
        brand_id: brandId,
        name: supplement.name,
        brand: supplement.brand,
        dosage: supplement.dosage,
        timing: supplement.timing,
        suggested_time: supplement.suggestedTime,
        icon: supplement.icon,
        active: true
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      brand: data.brand,
      dosage: data.dosage || '',
      timing: (data.timing as 'morning' | 'afternoon' | 'evening' | 'anytime') || 'anytime',
      suggestedTime: data.suggested_time || '08:00',
      icon: data.icon || '💊',
      active: data.active,
      addedAt: data.added_at
    };
  },

  // Update supplement
  async updateSupplement(supplementId: string, updates: Partial<Omit<UserSupplement, 'id' | 'addedAt'>>): Promise<void> {
    const dbUpdates: any = {};
    
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.brand !== undefined) dbUpdates.brand = updates.brand;
    if (updates.dosage !== undefined) dbUpdates.dosage = updates.dosage;
    if (updates.timing !== undefined) dbUpdates.timing = updates.timing;
    if (updates.suggestedTime !== undefined) dbUpdates.suggested_time = updates.suggestedTime;
    if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
    if (updates.active !== undefined) dbUpdates.active = updates.active;

    const { error } = await supabase
      .from('user_supplements')
      .update(dbUpdates)
      .eq('id', supplementId);

    if (error) throw error;
  },

  // Remove supplement (mark as inactive)
  async removeSupplement(supplementId: string): Promise<void> {
    const { error } = await supabase
      .from('user_supplements')
      .update({ active: false })
      .eq('id', supplementId);

    if (error) throw error;
  },

  // Get supplement brands
  async getSupplementBrands(): Promise<SupplementBrand[]> {
    // Return predefined brands list (already alphabetically ordered)
    return SUPPLEMENT_BRANDS.map((brandName, index) => ({
      id: `brand-${index + 1}`,
      name: brandName
    }));
  },

  // Add new brand
  async addSupplementBrand(name: string): Promise<SupplementBrand> {
    const { data, error } = await supabase
      .from('supplement_brands')
      .insert({ name })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};